import type { GeneratedDefinition, GeneratedNodeAttribute, NodeAttributeTarget, NodeAttributeType } from "@keydials/api-client";

// ---------------------------------------------------------------------------
// What the builder's Details stage offers (KEY-560, Phase 3), as data: the kinds of detail a dial can ask for, the places it can ask
// for them, and which settings go with which. The words belong to the stage in the user app, keyed by these ids; this says what
// each choice is in the payload, so the stage and its tests read one table rather than a chain of type checks.
// ---------------------------------------------------------------------------

export type DetailsTypeId = "shortText" | "longText" | "date" | "choice" | "person" | "email" | "phone" | "file" | "photo" | "video";

export interface DetailsType {
  id: DetailsTypeId;
  type: NodeAttributeType;
}

/** The kinds of detail the builder offers, in the order it offers them, with the field type each saves as. */
export const DETAILS_TYPES: readonly DetailsType[] = [
  { id: "shortText", type: "Text" },
  { id: "longText", type: "RichText" },
  { id: "date", type: "Date" },
  { id: "choice", type: "List" },
  { id: "person", type: "User" },
  { id: "email", type: "Email" },
  { id: "phone", type: "Phone" },
  { id: "file", type: "Document" },
  { id: "photo", type: "Image" },
  { id: "video", type: "Video" },
];

/**
 * The one field type the builder recognises but never offers. A table from a template is shown, and can be renamed, moved,
 * grouped and removed, but is never retyped and never added (decision 6).
 */
export const TABLE_TYPE = "Table" satisfies NodeAttributeType;

/**
 * The other field type the builder recognises but never offers (KEY-PEOPLE): a Person field picks from the dial's own Who's who
 * list, which the builder has no flag plumbing for. Treated exactly as a table — shown, renamed, moved, grouped and removed, never
 * retyped and never added.
 */
export const PERSON_TYPE = "Person" satisfies NodeAttributeType;

/**
 * And a third (KEY-752): a Contact field picks from the dial's own contacts list, behind the same people flag the builder has no
 * plumbing for. Treated exactly as a Person field — shown, renamed, moved, grouped and removed, never retyped and never added.
 */
export const CONTACT_TYPE = "Contact" satisfies NodeAttributeType;

/**
 * And a fourth (KEY-917): a Responsible person field picks a member or a contact, and its contacts half sits behind the same people
 * flag. Treated exactly as a Contact field — shown, renamed, moved, grouped and removed, never retyped and never added.
 */
export const RESPONSIBLE_TYPE = "ResponsiblePerson" satisfies NodeAttributeType;

/** How the builder reads a saved field's type: one of the kinds it offers, `"table"`, `"whoswho"`, `"contacts"` or `"responsible"`, or null for a type it doesn't know. */
export function detailsTypeOf(type: string): DetailsTypeId | "table" | "whoswho" | "contacts" | "responsible" | null {
  if (type === TABLE_TYPE) return "table";
  if (type === PERSON_TYPE) return "whoswho";
  if (type === CONTACT_TYPE) return "contacts";
  if (type === RESPONSIBLE_TYPE) return "responsible";
  return DETAILS_TYPES.find((t) => t.type === type)?.id ?? null;
}

export type DetailsPlaceId = "dial" | "section" | "actionPoint" | "item";

export interface DetailsPlace {
  id: DetailsPlaceId;
  target: NodeAttributeTarget;
  /** Only a dial with a checklist in each action point has items to ask about. */
  itemisedOnly: boolean;
}

/** The places a detail can be asked for: once for the whole dial, or on each section, action point or item. */
export const DETAILS_PLACES: readonly DetailsPlace[] = [
  { id: "dial", target: "Dial", itemisedOnly: false },
  { id: "section", target: "Section", itemisedOnly: false },
  { id: "actionPoint", target: "Segment", itemisedOnly: false },
  { id: "item", target: "Item", itemisedOnly: true },
];

/** The places this dial offers: each item only on a dial with checklists. */
export function placesFor(model: Pick<GeneratedDefinition, "kind">): DetailsPlace[] {
  return DETAILS_PLACES.filter((p) => !p.itemisedOnly || model.kind === "Itemised");
}

/** How the builder reads a saved field's level, or null for one it doesn't know. */
export function detailsPlaceOf(target: string): DetailsPlaceId | null {
  return DETAILS_PLACES.find((p) => p.target === target)?.id ?? null;
}

export type DetailsSettingId =
  | "options"
  | "peopleCanAddTheirOwn"
  | "group"
  | "startsFolded"
  | "suggestedAnswer"
  | "mustAnswerBeforeOpening"
  | "mustFillBeforeWork"
  | "onlySomeItems"
  | "allowMoreThanOne"
  | "whatToTellPeople";

export interface DetailsSetting {
  id: DetailsSettingId;
  /** What the setting writes on the field. */
  fields: readonly (keyof GeneratedNodeAttribute)[];
  /** The field types it's offered for; null for every type the builder recognises. */
  types: readonly NodeAttributeType[] | null;
  /** The levels it's offered at; null for every level. */
  targets: readonly NodeAttributeTarget[] | null;
  /** A condition on the field itself: while it's in a group, or while one of the "must be" switches is on. */
  offeredWhile?: "grouped" | "mustBeOn";
}

/** Every setting the Details stage offers, where it applies, and what it writes. */
export const DETAILS_SETTINGS: readonly DetailsSetting[] = [
  { id: "options", fields: ["options"], types: ["List"], targets: null },
  { id: "peopleCanAddTheirOwn", fields: ["allowUserValues"], types: ["List"], targets: null },
  { id: "group", fields: ["fieldsetTitle"], types: null, targets: null },
  // Folded is how a group arrives, so without a group there's nothing to fold.
  { id: "startsFolded", fields: ["fieldsetStartsCollapsed"], types: null, targets: null, offeredWhile: "grouped" },
  // Decision 3: the server keeps a suggested answer on these four types only.
  { id: "suggestedAnswer", fields: ["defaultValue"], types: ["Text", "RichText", "Date", "List"], targets: null },
  { id: "mustAnswerBeforeOpening", fields: ["requiredAtFirstRun"], types: null, targets: ["Dial"] },
  { id: "mustFillBeforeWork", fields: ["requiredForNode"], types: null, targets: ["Segment"] },
  { id: "onlySomeItems", fields: ["appliesToKeys"], types: null, targets: ["Item"] },
  { id: "allowMoreThanOne", fields: ["allowMultiple"], types: ["Document", "Image", "Person"], targets: null },
  // Decision 5: the words the field shows when it holds something up, so only at the levels with a "must be" switch. A template's
  // guidance on any other field is kept, not offered.
  { id: "whatToTellPeople", fields: ["guidance"], types: null, targets: ["Dial", "Segment"], offeredWhile: "mustBeOn" },
];

/** The settings a field of this type at this level can have, before any condition on the field itself. Empty for a type or level the builder doesn't know. */
export function settingsFor(type: string, target: string): DetailsSettingId[] {
  if (detailsTypeOf(type) === null || detailsPlaceOf(target) === null) return [];
  return DETAILS_SETTINGS.filter(
    (s) => (s.types === null || s.types.some((t) => t === type)) && (s.targets === null || s.targets.some((t) => t === target)),
  ).map((s) => s.id);
}

/** Whether the field holds something up until it's answered: the dial opening, or its action point being worked on. */
export function mustBeOn(field: GeneratedNodeAttribute): boolean {
  return (field.target === "Dial" && field.requiredAtFirstRun === true) || (field.target === "Segment" && field.requiredForNode === true);
}

/** The settings to show for this field as it stands: {@link settingsFor}, less those whose condition the field doesn't meet. */
export function offeredSettings(field: GeneratedNodeAttribute): DetailsSettingId[] {
  const grouped = Boolean(field.fieldsetTitle?.trim());
  return settingsFor(field.type, field.target).filter((id) => {
    const condition = DETAILS_SETTINGS.find((s) => s.id === id)?.offeredWhile;
    return condition === undefined || (condition === "grouped" ? grouped : mustBeOn(field));
  });
}
